import { HttpErrorResponse } from '@angular/common/http';
import { ProviderType } from '../../models/project-integration.model';
import { providerLabel } from '../../shared/provider-meta';
import { mapBugCreateError } from '../../shared/tracker-error';

/** Die Meldungen, mit denen der Bug-Dialog eine abgewiesene Anlage erklaert (NEX-1234). */
export const LIVE_BUG_ERROR_KEYS = {
  tokenMissing: 'testRunner.live.bugDialog.error.tokenMissing',
  noConnection: 'testRunner.live.bugDialog.error.noConnection',
  alreadyLinked: 'testRunner.live.bugDialog.error.alreadyLinked',
  notSupported: 'testRunner.live.bugDialog.error.notSupported',
  unknown: 'testRunner.live.bugDialog.error.unknown',
} as const;

/** Was der Dialog von einer abgewiesenen Anlage zeigt. */
export interface LiveBugError {
  key: string;
  params: Record<string, string>;
  /** Nur ein fehlendes oder abgelehntes Token laesst sich im Konto beheben. */
  showsAccountLink: boolean;
  /** Am Fall haengt schon ein Vorgang — ein zweiter Versuch legte keinen an, er scheiterte erneut. */
  alreadyLinked: boolean;
}

/** Die Codes des {@code ApiExceptionHandler} fuer ein fehlendes Token, je Tracker. */
const TOKEN_MISSING: Record<string, ProviderType> = {
  JIRA_TOKEN_MISSING: 'JIRA',
  REDMINE_TOKEN_MISSING: 'REDMINE',
};

function failure(key: string, extra: Partial<LiveBugError> = {}): LiveBugError {
  return { key, params: {}, showsAccountLink: false, alreadyLinked: false, ...extra };
}

/**
 * Uebersetzt die Absage des Servers in eine Meldung. Ein 412 ist dort zweierlei: mit
 * {@code *_TOKEN_MISSING} fehlt der Person das Token, ohne Token-Code (etwa
 * {@code NO_ACTIVE_INTEGRATION}) dem Projekt die aktive Verbindung — nur im ersten Fall
 * fuehrt der Weg ins Konto weiter.
 *
 * <p>Absagen des Trackers mit eigenem Code (Token abgelehnt, keine Berechtigung, Verbindung
 * deaktiviert, Vorgang abgelehnt) gehen vor; {@code boardUrl} nennt den Host des Ziels (NEX-1354).
 * Erst ein 409 ohne Code heisst „bereits verknuepft“; er traegt keinen Schluessel, den bestehenden
 * Vorgang holt die Ansicht mit dem neuen Stand des Durchlaufs.
 */
export function liveBugError(error: HttpErrorResponse, boardUrl?: string | null): LiveBugError {
  const tracker = mapBugCreateError(error, boardUrl);
  if (tracker) {
    return failure(tracker.key, { params: tracker.params, showsAccountLink: tracker.accountLink });
  }
  if (error.status === 409) {
    return failure(LIVE_BUG_ERROR_KEYS.alreadyLinked, { alreadyLinked: true });
  }
  if (error.status === 501) {
    return failure(LIVE_BUG_ERROR_KEYS.notSupported);
  }
  if (error.status === 412) {
    const code = (error.error as { code?: string } | null)?.code ?? '';
    const provider = TOKEN_MISSING[code];
    return provider
      ? failure(LIVE_BUG_ERROR_KEYS.tokenMissing, {
          params: { provider: providerLabel(provider) },
          showsAccountLink: true,
        })
      : failure(LIVE_BUG_ERROR_KEYS.noConnection);
  }
  return failure(LIVE_BUG_ERROR_KEYS.unknown);
}
